import httpClient from "./httpClient";

const PUSH_COMPAT_POPUP_NAME = "flamex-push-activation";
const PUSH_BRIDGE_MESSAGE_SOURCE = "flamex-push-bridge";
const PUSH_BRIDGE_STORAGE_KEY = "flamex.push.bridge_state";
const PUSH_VAPID_PUBLIC_KEY_STORAGE_KEY = "flamex.push.vapid_public_key";
export const APP_NOTIFICATION_CREATED_EVENT = "flamex:notification-created";

const APPROVAL_NOTIFICATION_EVENT_CODES = new Set([
  "CHECK_APPROVAL_REQUIRED",
  "CHECK_APPROVED",
  "CHECK_REPROVED",
]);

export async function listarNotificacoes({
  page = 0,
  size = 20,
  readStatus = "ALL",
  dateFrom = "",
  dateTo = "",
} = {}) {
  const response = await httpClient.get("/api/notificacoes", {
    params: {
      page,
      size,
      readStatus,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    },
  });
  return response.data;
}

export async function obterContagemNaoLidas() {
  const response = await httpClient.get("/api/notificacoes/unread-count");
  return Number(response.data?.unreadCount || 0);
}

export async function marcarNotificacaoComoLida(notificationId) {
  const response = await httpClient.post(`/api/notificacoes/${notificationId}/read`);
  return response.data;
}

export async function marcarTodasNotificacoesComoLidas() {
  const response = await httpClient.post("/api/notificacoes/read-all");
  return response.data;
}

export async function listarPoliticasNotificacao() {
  const response = await httpClient.get("/api/notificacoes/policies");
  return Array.isArray(response.data) ? response.data : [];
}

export async function atualizarPoliticaNotificacao(eventCode, payload) {
  const response = await httpClient.put(`/api/notificacoes/policies/${eventCode}`, payload);
  return response.data;
}

export async function obterChavePublicaPush() {
  try {
    const response = await httpClient.get("/api/notificacoes/push/public-key");
    if (response.data?.publicKey) {
      cacheVapidPublicKey(response.data.publicKey);
    }
    return response.data;
  } catch (error) {
    const cachedPublicKey = getCachedVapidPublicKey();
    if (cachedPublicKey) {
      return {
        enabled: true,
        publicKey: cachedPublicKey,
      };
    }

    throw error;
  }
}

export async function obterStatusPush(endpoint = "") {
  const response = await httpClient.get("/api/notificacoes/push/status", {
    params: {
      endpoint: endpoint || undefined,
    },
  });
  return response.data;
}

export async function obterDiagnosticoServidorPush() {
  const response = await httpClient.get("/api/notificacoes/push/diagnostics");
  return response.data;
}

export async function enviarPushTeste() {
  const response = await httpClient.post("/api/notificacoes/push/test");
  return response.data;
}

export async function salvarInscricaoPush(subscription) {
  const json = typeof subscription?.toJSON === "function" ? subscription.toJSON() : subscription;
  const keys = json?.keys || {};
  await httpClient.post("/api/notificacoes/push/subscriptions", {
    endpoint: json?.endpoint,
    p256dh: keys.p256dh,
    auth: keys.auth,
    expirationTime: json?.expirationTime ?? null,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
  });
}

export async function removerInscricaoPush(endpoint) {
  await httpClient.delete("/api/notificacoes/push/subscriptions", {
    data: { endpoint },
  });
}

export function publishClientNotificationCreated(notification) {
  if (typeof window === "undefined" || !notification) {
    return;
  }

  window.dispatchEvent(new CustomEvent(APP_NOTIFICATION_CREATED_EVENT, {
    detail: notification,
  }));
}

export function isApprovalNotification(notification) {
  return APPROVAL_NOTIFICATION_EVENT_CODES.has(String(notification?.eventCode || "").trim().toUpperCase());
}

export function isApprovalPendingNotification(notification) {
  return String(notification?.eventCode || "").trim().toUpperCase() === "CHECK_APPROVAL_REQUIRED";
}

export function getNotificationTaskId(notification) {
  const taskId = notification?.payload?.taskId ?? notification?.entityId ?? null;
  return Number.isFinite(Number(taskId)) ? Number(taskId) : null;
}

export function getNotificationPartnerIds(notification) {
  const rawIds = notification?.payload?.partnerIds;
  if (!Array.isArray(rawIds)) {
    return [];
  }

  return rawIds
    .map((value) => Number(value))
    .filter(Number.isFinite);
}

export function construirUrlWebSocketNotificacoes() {
  const baseURL = httpClient.defaults.baseURL || window.location.origin;
  const url = new URL(baseURL, window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/ws/notifications";
  url.search = "";
  url.hash = "";
  return url.toString();
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

export async function ensureNotificationServiceWorker({
  scriptUrls = ["/push-service-worker.js", "/service-worker.js"],
  scope = "/",
} = {}) {
  if (!isPushSupported()) {
    return null;
  }

  let lastError = null;
  for (const scriptUrl of scriptUrls) {
    try {
      const registration = await navigator.serviceWorker.register(scriptUrl, {
        scope,
        updateViaCache: "none",
      });
      await waitForActiveRegistration(registration);
      const readyRegistration = await navigator.serviceWorker.ready.catch(() => null);
      return readyRegistration || registration;
    } catch (error) {
      lastError = error;
    }
  }

  throw new Error(
    lastError instanceof Error
      ? `Nao foi possivel registrar o service worker do Web Push. ${lastError.message}`
      : "Nao foi possivel registrar o service worker do Web Push."
  );
}

export async function obterEstadoPushAtual() {
  const diagnostics = await obterDiagnosticoPushAtual();
  return {
    supported: diagnostics.supported,
    permission: diagnostics.permission,
    subscribed: diagnostics.subscribed,
    subscription: diagnostics.subscription,
  };
}

export async function obterDiagnosticoPushAtual() {
  const bridgeState = getCachedPushBridgeState();
  const diagnostics = {
    supported: isPushSupported(),
    permission: bridgeState?.permission || "unknown",
    subscribed: Boolean(bridgeState?.subscribed),
    subscription: bridgeState?.subscription || null,
    serviceWorkerRegistered: Boolean(bridgeState?.serviceWorkerRegistered),
    serviceWorkerActive: Boolean(bridgeState?.serviceWorkerActive),
    serviceWorkerScope: bridgeState?.serviceWorkerScope || null,
    browserEndpoint: bridgeState?.browserEndpoint || null,
    browserEndpointSuffix: bridgeState?.browserEndpointSuffix || null,
    browserError: bridgeState?.browserError || null,
    bridgeOrigin: bridgeState?.bridgeOrigin || null,
    appBase: bridgeState?.appBase || null,
    lastAction: bridgeState?.lastAction || null,
    lastStage: bridgeState?.lastStage || null,
    lastUpdatedAt: bridgeState?.updatedAt || null,
    traceLines: Array.isArray(bridgeState?.traceLines) ? bridgeState.traceLines : [],
    keyLength: bridgeState?.keyLength ?? null,
    decodedKeyLength: bridgeState?.decodedKeyLength ?? null,
    decodedKeyFirstByte: bridgeState?.decodedKeyFirstByte ?? null,
    existingSubscriptionEndpointSuffix: bridgeState?.existingSubscriptionEndpointSuffix || null,
    lastErrorName: bridgeState?.lastErrorName || null,
    lastErrorMessage: bridgeState?.lastErrorMessage || null,
    lastErrorStage: bridgeState?.lastErrorStage || null,
    backendStatus: null,
    backendError: null,
    serverDiagnostics: null,
    serverDiagnosticsError: null,
  };

  if (!diagnostics.supported) {
    return diagnostics;
  }

  try {
    diagnostics.backendStatus = await obterStatusPush(diagnostics.browserEndpoint || "");
    if (!diagnostics.subscribed && diagnostics.backendStatus?.currentEndpointRegistered) {
      diagnostics.subscribed = true;
    }
  } catch (error) {
    diagnostics.backendError =
      error?.response?.data?.message ||
      (error instanceof Error ? error.message : "Nao foi possivel consultar o backend do Web Push");
  }

  try {
    diagnostics.serverDiagnostics = await obterDiagnosticoServidorPush();
  } catch (error) {
    diagnostics.serverDiagnosticsError =
      error?.response?.data?.message ||
      (error instanceof Error ? error.message : "Nao foi possivel consultar o diagnostico VAPID no backend");
  }

  return diagnostics;
}

export async function sincronizarInscricaoPushAtual() {
  const bridgeState = getCachedPushBridgeState();
  const subscription = bridgeState?.subscription || null;
  if (!subscription) {
    return null;
  }

  await salvarInscricaoPush(subscription);
  return subscription;
}

export async function ativarWebPush() {
  if (!isPushSupported()) {
    throw new Error("Web Push nao e suportado neste navegador");
  }

  const traceLines = [];
  let stage = "init";
  const trace = (message, payload) => {
    const line =
      payload !== undefined
        ? `[${new Date().toISOString()}] ${message}: ${safeSerialize(payload)}`
        : `[${new Date().toISOString()}] ${message}`;
    traceLines.push(line);
  };
  const setStage = (nextStage, payload) => {
    stage = nextStage;
    trace(`stage=${nextStage}`, payload);
  };

  try {
    setStage("permission-request", { current: Notification.permission });
    const permission = await Notification.requestPermission();
    trace("permission", permission);
    if (permission !== "granted") {
      throw new Error("Permissao de notificacao nao concedida.");
    }

    setStage("public-key-fetch");
    const publicKeyData = await obterChavePublicaPush();
    const publicKey = sanitizeVapidPublicKey(publicKeyData?.publicKey || "");
    if (!publicKeyData?.enabled) {
      throw new Error("Web Push desativado no servidor. Configure as chaves VAPID (public/private) no backend.");
    }
    if (!isLikelyValidVapidPublicKey(publicKey)) {
      throw new Error("O backend nao retornou uma chave publica valida do Web Push.");
    }

    const applicationServerKey = base64UrlToUint8Array(publicKey);
    trace("public key decoded", {
      length: publicKey.length,
      decodedLength: applicationServerKey.length,
      firstByte: applicationServerKey[0],
    });

    setStage("service-worker-register");
    const registration = await ensureNotificationServiceWorker();
    trace("service worker ready", {
      scope: registration?.scope || null,
      activeScript: registration?.active?.scriptURL || null,
    });

    setStage("subscription-check");
    let subscription = await registration.pushManager.getSubscription();
    trace("existing subscription", { endpoint: subscription?.endpoint || null });

    if (subscription && !subscriptionMatchesApplicationServerKey(subscription, applicationServerKey)) {
      await subscription.unsubscribe().catch(() => false);
      trace("existing subscription removed due to key mismatch", {
        endpoint: subscription?.endpoint || null,
      });
      subscription = null;
    }

    if (!subscription) {
      setStage("subscription-create", {
        userVisibleOnly: true,
        applicationServerKeyLength: applicationServerKey.length,
      });
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      trace("subscription created", { endpoint: subscription?.endpoint || null });
    }

    setStage("subscription-save");
    await salvarInscricaoPush(subscription);

    const backendStatus = await obterStatusPush(subscription?.endpoint || "").catch(() => null);
    trace("backend status", backendStatus);

    const bridgeState = buildBridgeState({
      registration,
      subscription,
      backendStatus,
      traceLines,
      lastAction: "activate",
      lastStage: stage,
      keyLength: publicKey.length,
      decodedKeyLength: applicationServerKey.length,
      decodedKeyFirstByte: applicationServerKey[0],
    });

    persistPushBridgeState(bridgeState);
    return bridgeState;
  } catch (error) {
    const message = `${error?.name || "Erro"}: ${error?.message || String(error)}`;
    trace("error", { stage, message });

    const bridgeState = buildBridgeState({
      registration: null,
      subscription: null,
      backendStatus: await obterStatusPush("").catch(() => null),
      browserError: message,
      traceLines,
      lastAction: "activate",
      lastStage: stage,
    });
    bridgeState.lastErrorName = error?.name || "Erro";
    bridgeState.lastErrorMessage = error?.message || String(error);
    bridgeState.lastErrorStage = stage;
    persistPushBridgeState(bridgeState);
    throw error;
  }
}

export async function desativarWebPush() {
  if (!isPushSupported()) {
    throw new Error("Web Push nao e suportado neste navegador");
  }

  const traceLines = [];
  let stage = "deactivate-start";
  const trace = (message, payload) => {
    const line =
      payload !== undefined
        ? `[${new Date().toISOString()}] ${message}: ${safeSerialize(payload)}`
        : `[${new Date().toISOString()}] ${message}`;
    traceLines.push(line);
  };

  let registration = null;
  let subscription = null;
  try {
    registration = await navigator.serviceWorker.getRegistration("/").catch(() => null);
    subscription = registration ? await registration.pushManager.getSubscription().catch(() => null) : null;
    trace("current subscription", { endpoint: subscription?.endpoint || null });

    if (subscription?.endpoint) {
      await removerInscricaoPush(subscription.endpoint).catch(() => null);
      await subscription.unsubscribe().catch(() => false);
    }

    const backendStatus = await obterStatusPush("").catch(() => null);
    const bridgeState = buildBridgeState({
      registration,
      subscription: null,
      backendStatus,
      traceLines,
      lastAction: "deactivate",
      lastStage: stage,
      existingSubscriptionEndpointSuffix: maskEndpoint(subscription?.endpoint || null),
    });
    persistPushBridgeState(bridgeState);
    return bridgeState;
  } catch (error) {
    const message = `${error?.name || "Erro"}: ${error?.message || String(error)}`;
    trace("error", { stage, message });

    const bridgeState = buildBridgeState({
      registration,
      subscription: null,
      backendStatus: await obterStatusPush("").catch(() => null),
      browserError: message,
      traceLines,
      lastAction: "deactivate",
      lastStage: stage,
    });
    bridgeState.lastErrorName = error?.name || "Erro";
    bridgeState.lastErrorMessage = error?.message || String(error);
    bridgeState.lastErrorStage = stage;
    persistPushBridgeState(bridgeState);
    throw error;
  }
}

export async function resetarAmbientePush() {
  if (!isPushSupported()) {
    throw new Error("Web Push nao e suportado neste navegador");
  }

  const traceLines = [];
  let stage = "reset-start";
  const trace = (message, payload) => {
    const line =
      payload !== undefined
        ? `[${new Date().toISOString()}] ${message}: ${safeSerialize(payload)}`
        : `[${new Date().toISOString()}] ${message}`;
    traceLines.push(line);
  };

  try {
    const registrations = await navigator.serviceWorker.getRegistrations().catch(() => []);
    trace("registrations found", { count: registrations.length });

    for (const registration of registrations) {
      try {
        const subscription = await registration.pushManager.getSubscription().catch(() => null);
        if (subscription?.endpoint) {
          await removerInscricaoPush(subscription.endpoint).catch(() => null);
          await subscription.unsubscribe().catch(() => false);
        }
      } catch {
        // Sem acao adicional.
      }

      await registration.unregister().catch(() => false);
    }

    if (typeof window !== "undefined" && "caches" in window) {
      const cacheKeys = await caches.keys().catch(() => []);
      await Promise.all(cacheKeys.map((key) => caches.delete(key).catch(() => false)));
    }

    clearCachedPushBridgeState();

    const bridgeState = buildBridgeState({
      registration: null,
      subscription: null,
      backendStatus: await obterStatusPush("").catch(() => null),
      traceLines,
      lastAction: "reset",
      lastStage: stage,
    });
    persistPushBridgeState(bridgeState);
    return bridgeState;
  } catch (error) {
    const message = `${error?.name || "Erro"}: ${error?.message || String(error)}`;
    trace("error", { stage, message });

    const bridgeState = buildBridgeState({
      registration: null,
      subscription: null,
      backendStatus: await obterStatusPush("").catch(() => null),
      browserError: message,
      traceLines,
      lastAction: "reset",
      lastStage: stage,
    });
    bridgeState.lastErrorName = error?.name || "Erro";
    bridgeState.lastErrorMessage = error?.message || String(error);
    bridgeState.lastErrorStage = stage;
    persistPushBridgeState(bridgeState);
    throw error;
  }
}

export function prepararJanelaCompatibilidadePush() {
  if (typeof window === "undefined") {
    return null;
  }

  const popup = window.open("", PUSH_COMPAT_POPUP_NAME, "popup=yes,width=460,height=680,resizable=yes,scrollbars=yes");
  if (popup) {
    popup.document.title = "Ativando Web Push";
    popup.document.body.innerHTML =
      "<div style=\"font-family:sans-serif;padding:24px;background:#0b1220;color:#f8fafc;min-height:100vh;box-sizing:border-box;\">" +
      "<h2 style=\"margin:0 0 12px;\">Gerenciando Web Push</h2>" +
      "<p style=\"margin:0;color:#cbd5e1;line-height:1.5;\">Preparando o navegador para concluir a operacao...</p>" +
      "</div>";
  }
  return popup;
}

function buildBridgeState({
  registration,
  subscription,
  backendStatus,
  browserError = null,
  traceLines = [],
  lastAction = null,
  lastStage = null,
  keyLength = null,
  decodedKeyLength = null,
  decodedKeyFirstByte = null,
  existingSubscriptionEndpointSuffix = null,
} = {}) {
  const json = typeof subscription?.toJSON === "function" ? subscription.toJSON() : subscription;
  return {
    permission: typeof Notification !== "undefined" ? Notification.permission : "unsupported",
    subscribed: Boolean(json?.endpoint),
    subscription: json || null,
    serviceWorkerRegistered: Boolean(registration),
    serviceWorkerActive: Boolean(registration?.active),
    serviceWorkerScope: registration?.scope || null,
    browserEndpoint: json?.endpoint || null,
    browserEndpointSuffix: maskEndpoint(json?.endpoint),
    browserError,
    backendStatus: backendStatus || null,
    bridgeOrigin: typeof window !== "undefined" ? window.location.origin : null,
    appBase: typeof window !== "undefined" ? window.location.origin : null,
    lastAction,
    lastStage,
    traceLines: Array.isArray(traceLines) ? [...traceLines] : [],
    updatedAt: new Date().toISOString(),
    keyLength,
    decodedKeyLength,
    decodedKeyFirstByte,
    existingSubscriptionEndpointSuffix: existingSubscriptionEndpointSuffix || null,
  };
}

async function executarAcaoPushEmJanelaAuxiliar(action, compatWindow) {
  if (!isPushSupported()) {
    throw new Error("Web Push nao e suportado neste navegador");
  }

  const popup = compatWindow || prepararJanelaCompatibilidadePush();
  if (!popup || popup.closed) {
    throw new Error("Nao foi possivel abrir a janela auxiliar do Web Push.");
  }

  const bridgeOrigin = getPushBridgeOrigin();
  const bridgeUrl = new URL("/push-activate.html", bridgeOrigin);
  bridgeUrl.searchParams.set("action", action);
  bridgeUrl.searchParams.set("appBase", window.location.origin);
  bridgeUrl.searchParams.set("openerOrigin", window.location.origin);

  popup.location.replace(bridgeUrl.toString());
  popup.focus();

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      closeCompatWindow(popup);
      reject(new Error("A janela auxiliar demorou para concluir a operacao do Web Push."));
    }, 90000);

    const closedPollId = window.setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new Error("A operacao do Web Push foi cancelada antes de terminar."));
      }
    }, 500);

    const handleMessage = (event) => {
      if (event.origin !== bridgeOrigin || event.source !== popup) {
        return;
      }

      const payload = event.data;
      if (!payload || payload.source !== PUSH_BRIDGE_MESSAGE_SOURCE) {
        return;
      }

      cleanup();

      if (payload.bridgeState) {
        persistPushBridgeState(payload.bridgeState);
      } else if (action === "deactivate" || action === "reset") {
        clearCachedPushBridgeState();
      }

      if (payload.status === "success") {
        closeCompatWindow(popup);
        resolve(payload.bridgeState || true);
        return;
      }

      reject(new Error(payload.message || "Nao foi possivel concluir a operacao do Web Push."));
    };

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(closedPollId);
      window.removeEventListener("message", handleMessage);
    };

    window.addEventListener("message", handleMessage);
  });
}

function getPushBridgeOrigin() {
  const baseURL = httpClient.defaults.baseURL || window.location.origin;
  return new URL(baseURL, window.location.origin).origin;
}

function cacheVapidPublicKey(publicKey) {
  if (typeof window === "undefined") {
    return;
  }

  const normalized = sanitizeVapidPublicKey(publicKey);
  if (!isLikelyValidVapidPublicKey(normalized)) {
    return;
  }

  try {
    window.sessionStorage.setItem(PUSH_VAPID_PUBLIC_KEY_STORAGE_KEY, normalized);
  } catch {
    // Sem acao adicional.
  }

  try {
    window.localStorage.setItem(PUSH_VAPID_PUBLIC_KEY_STORAGE_KEY, normalized);
  } catch {
    // Sem acao adicional.
  }
}

function getCachedVapidPublicKey() {
  if (typeof window === "undefined") {
    return "";
  }

  const candidates = [];

  try {
    candidates.push(window.sessionStorage.getItem(PUSH_VAPID_PUBLIC_KEY_STORAGE_KEY));
  } catch {
    // Sem acao adicional.
  }

  try {
    candidates.push(window.localStorage.getItem(PUSH_VAPID_PUBLIC_KEY_STORAGE_KEY));
  } catch {
    // Sem acao adicional.
  }

  const cached = candidates.find((candidate) => isLikelyValidVapidPublicKey(candidate));
  return cached ? sanitizeVapidPublicKey(cached) : "";
}

function sanitizeVapidPublicKey(publicKey) {
  return `${publicKey || ""}`.trim().replace(/\s+/g, "");
}

function isLikelyValidVapidPublicKey(publicKey) {
  const normalized = sanitizeVapidPublicKey(publicKey);
  return /^[A-Za-z0-9_-]+$/.test(normalized) && normalized.length >= 80;
}

function base64UrlToUint8Array(base64String) {
  const sanitized = sanitizeVapidPublicKey(base64String);
  if (!isLikelyValidVapidPublicKey(sanitized)) {
    throw new Error("A chave publica VAPID recebida esta ausente ou invalida.");
  }

  const normalized = sanitized.replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (normalized.length % 4 || 4)) % 4);
  const binaryString = window.atob(`${normalized}${padding}`);
  const outputArray = new Uint8Array(binaryString.length);
  for (let index = 0; index < binaryString.length; index += 1) {
    outputArray[index] = binaryString.charCodeAt(index);
  }
  return outputArray;
}

function safeSerialize(value) {
  try {
    if (typeof value === "string") {
      return value;
    }
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function toUint8Array(value) {
  if (value instanceof Uint8Array) {
    return value;
  }
  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  return new Uint8Array(0);
}

function subscriptionMatchesApplicationServerKey(subscription, expectedKey) {
  if (!subscription?.options?.applicationServerKey || !expectedKey) {
    return true;
  }

  const actualKey = toUint8Array(subscription.options.applicationServerKey);
  if (actualKey.length !== expectedKey.length) {
    return false;
  }

  return actualKey.every((value, index) => value === expectedKey[index]);
}

async function waitForActiveRegistration(registration) {
  if (registration?.active) {
    return registration;
  }

  const worker = registration?.installing || registration?.waiting;
  if (!worker) {
    return registration;
  }

  await new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error("O service worker do push demorou para ativar.")), 10000);
    const handleStateChange = () => {
      if (worker.state === "activated" || registration?.active) {
        window.clearTimeout(timeoutId);
        worker.removeEventListener("statechange", handleStateChange);
        resolve();
      }
    };
    worker.addEventListener("statechange", handleStateChange);
    handleStateChange();
  });

  return registration;
}

function getCachedPushBridgeState() {
  if (typeof window === "undefined") {
    return null;
  }

  const rawCandidates = [];

  try {
    rawCandidates.push(window.sessionStorage.getItem(PUSH_BRIDGE_STORAGE_KEY));
  } catch {
    // Sem acao adicional.
  }

  try {
    rawCandidates.push(window.localStorage.getItem(PUSH_BRIDGE_STORAGE_KEY));
  } catch {
    // Sem acao adicional.
  }

  for (const raw of rawCandidates) {
    if (!raw) {
      continue;
    }

    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return parsed;
      }
    } catch {
      // Sem acao adicional.
    }
  }

  return null;
}

function persistPushBridgeState(bridgeState) {
  if (typeof window === "undefined" || !bridgeState || typeof bridgeState !== "object") {
    return;
  }

  const normalized = {
    ...bridgeState,
    browserEndpointSuffix: bridgeState.browserEndpointSuffix || maskEndpoint(bridgeState.browserEndpoint),
  };
  const serialized = JSON.stringify(normalized);

  try {
    window.sessionStorage.setItem(PUSH_BRIDGE_STORAGE_KEY, serialized);
  } catch {
    // Sem acao adicional.
  }

  try {
    window.localStorage.setItem(PUSH_BRIDGE_STORAGE_KEY, serialized);
  } catch {
    // Sem acao adicional.
  }
}

function clearCachedPushBridgeState() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(PUSH_BRIDGE_STORAGE_KEY);
  } catch {
    // Sem acao adicional.
  }

  try {
    window.localStorage.removeItem(PUSH_BRIDGE_STORAGE_KEY);
  } catch {
    // Sem acao adicional.
  }
}

function closeCompatWindow(compatWindow) {
  try {
    compatWindow?.close?.();
  } catch {
    // Sem acao adicional.
  }
}

function maskEndpoint(endpoint) {
  if (!endpoint) {
    return null;
  }

  const normalized = String(endpoint);
  const visibleLength = Math.min(24, normalized.length);
  return normalized.slice(-visibleLength);
}
